/**
 * packages/lsp/src/server-core.ts
 *
 * Transport-agnostic LSP server wiring. Constructs ArchlangServices,
 * hooks up document lifecycle, publishes diagnostics. Filesystem
 * access is mediated by LspIO so this module runs both in node (over
 * stdio) and in a browser worker (over MessagePort).
 *
 * What lives here:
 *   - Every protocol handler the node server.ts currently wires up.
 *   - Workspace initial scan (walk → setFile) via LspIO.
 *   - archlang.json + VSCode settings merge.
 *   - Diagnostic publication for open + watched files.
 *
 * What does NOT live here (stays in the node/browser entry):
 *   - createConnection / transport setup.
 *   - Bundled-stdlib resolution (lives on LspIO.defaultStdlibUri).
 *   - process.cwd / process.env reads.
 *   - chokidar / fs.watch — the language client drives change events
 *     via DidChangeWatchedFilesNotification, which is transport-agnostic
 *     and handled here.
 */

import {
  TextDocuments,
  TextDocumentSyncKind,
  DidChangeWatchedFilesNotification,
  FileChangeType,
  type Connection,
  type InitializeParams,
  type InitializeResult,
} from "vscode-languageserver";
import { TextDocument } from "vscode-languageserver-textdocument";
import {
  SEMANTIC_TOKEN_TYPES,
  SEMANTIC_TOKEN_MODIFIERS,
} from "./providers/semantic-tokens.js";
import { ArchlangServices } from "./services.js";
import {
  FULL_COMPLETION_CAPS,
  type ClientCompletionCaps,
} from "./providers/completion.js";
import {
  DEFAULT_CONFIG,
  loadProjectConfigFromUri,
  mergeConfig,
  type ArchlangConfig,
  type RawConfig,
} from "./workspace/config.js";
import { PackageRouter } from "./workspace/package-router.js";
import {
  provideNavTree,
  MODEL_CHANGED_NOTIFICATION,
  NAV_TREE_REQUEST,
  type NavTreeParams,
} from "./providers/nav-tree.js";
import {
  provideEntityDetails,
  ENTITY_DETAILS_REQUEST,
  type EntityDetailsParams,
} from "./providers/entity-details.js";
import { registerLanguagePack, sloLanguagePack } from "@archlang/engine";
import { isMarkdownPath } from "@archlang/viewer-core";
import type { LspIO } from "./io.js";

/** A rebuild fires per file; a tree only needs one "something changed" per
 *  burst. Longer than the parse debounce so a multi-file save coalesces. */
const MODEL_CHANGED_DEBOUNCE_MS = 250;

export interface ServerCoreOptions {
  readonly connection: Connection;
  readonly io: LspIO;
}

/**
 * Wire an ArchlangServices instance to the given Connection using the
 * supplied LspIO for all filesystem access. Mirrors the behaviour of
 * the historical node `startServer` exactly — only the I/O surface
 * differs.
 */
export function startServerCore({ connection, io }: ServerCoreOptions): void {
  // Light language packs the server ships: the telemetry SLO pack contributes a
  // procedure (`observed.p95 > slo.p95`) + docs. Registered once at boot; its
  // procedures surface in every package's diagnostic stream (zero false
  // positives on a model that carries neither aspect).
  registerLanguagePack(sloLanguagePack);

  const documents = new TextDocuments(TextDocument);

  // One package router per workspace folder. A router discovers every package
  // under its folder and gives each its own single-package services, so
  // same-named modules across packages don't collide (each package is its own
  // resolution unit). Single-package trees collapse to one services — identical
  // to the pre-router flat behavior.
  let routers: PackageRouter[] = [];
  let currentConfig: ArchlangConfig = DEFAULT_CONFIG;
  // Empty fallback for requests that arrive before bootstrap (shouldn't happen
  // — handlers fire after onInitialized — but keeps routing total).
  const fallback = new ArchlangServices();
  const logger = { warn: (m: string) => connection.console.warn(m) };

  /** The package services that owns a file URI: the router whose folder is the
   *  deepest ancestor, then that router's owner package for the file. Routing
   *  ONLY — see {@link servicesForUri}, which is what handlers should use. */
  const routeToServices = (uri: string): ArchlangServices => {
    let best: PackageRouter | undefined;
    let bestLen = -1;
    for (const r of routers) {
      const base = r.folderUri.endsWith("/") ? r.folderUri.slice(0, -1) : r.folderUri;
      if (uri === base || uri.startsWith(base + "/")) {
        if (base.length > bestLen) {
          best = r;
          bestLen = base.length;
        }
      }
    }
    const r = best ?? routers[0];
    return r ? r.servicesForUri(uri) : fallback;
  };

  /**
   * The services for a URI, with any pending edit to it already applied.
   *
   * The flush is here rather than in each handler deliberately. It used to be
   * opt-in — completion and signature help called `flushPending`, on the
   * reasoning that they are the requests that fire WHILE typing — and every
   * other read could answer from the parse an armed debounce was about to
   * replace. That reasoning had the question backwards: what matters is not how
   * likely a request is to land inside the 100 ms window, but what happens when
   * one does. A client CACHES the answer against the buffer's version, so a
   * single unlucky read pins a stale answer until the next keystroke — see
   * `read-inside-debounce.test.ts`, and the live trace that found it.
   *
   * Making it structural also means the seventeenth request handler cannot
   * forget: there is no way to reach a package's services for a URI without the
   * workspace being current for that URI first.
   */
  const servicesForUri = (uri: string): ArchlangServices => {
    flushPending(uri);
    return routeToServices(uri);
  };

  /** Every live package services across every folder. */
  const allServices = (): ArchlangServices[] => routers.flatMap((r) => r.services());

  // A host rendering navigation natively (an IDE tree) needs to know when a
  // rebuild may have changed it. The server publishes nothing about the model
  // besides diagnostics, so without this the host can only poll. Payload-free:
  // it re-requests what it currently shows. Coalesced, because a rebuild fires
  // per file and a tree only needs to hear "something changed" once per burst.
  let modelChangedTimer: ReturnType<typeof setTimeout> | undefined;
  const notifyModelChanged = (): void => {
    if (modelChangedTimer) clearTimeout(modelChangedTimer);
    modelChangedTimer = setTimeout(() => {
      modelChangedTimer = undefined;
      try {
        void connection.sendNotification(MODEL_CHANGED_NOTIFICATION);
      } catch {
        // `sendNotification` throws SYNCHRONOUSLY on a closed or disposed
        // connection, which `void` does not catch — and a window reload can
        // close it inside this very debounce window. An uncaught throw out of
        // a timer takes the server down.
      }
    }, MODEL_CHANGED_DEBOUNCE_MS);
  };

  connection.onRequest(NAV_TREE_REQUEST, (params: NavTreeParams | null) =>
    provideNavTree(routers, params ?? {}),
  );

  connection.onRequest(ENTITY_DETAILS_REQUEST, (params: EntityDetailsParams | null) =>
    provideEntityDetails(routers, params ?? { id: "" }),
  );

  let workspaceFolders: string[] = [];
  // Which modern CompletionItem fields the connected client can consume — read
  // from the initialize handshake (P2b). Default OFF for any flag the client
  // doesn't advertise, so the server never emits a field the client will choke
  // on. `completion()` strips the rest before it hits the wire.
  let clientCompletionCaps: ClientCompletionCaps = FULL_COMPLETION_CAPS;
  // The editor's display language, for localising diagnostic messages. Set once
  // at initialize; a language change in the editor triggers a restart. Seeded
  // from ARCHLANG_LOCALE (the IntelliJ plugin passes the IDE language this way,
  // since LSP4IJ doesn't populate InitializeParams.locale); params.locale wins
  // when the client sends it (vscode-languageclient does).
  let currentLocale: string | undefined =
    typeof process !== "undefined" ? process.env?.ARCHLANG_LOCALE || undefined : undefined;

  connection.onInitialize((params: InitializeParams): InitializeResult => {
    if (params.workspaceFolders) {
      workspaceFolders = params.workspaceFolders.map((f) => f.uri);
    } else if (params.rootUri) {
      workspaceFolders = [params.rootUri];
    }
    currentLocale = params.locale ?? currentLocale;

    const ci = params.capabilities?.textDocument?.completion?.completionItem;
    clientCompletionCaps = {
      labelDetails: ci?.labelDetailsSupport ?? false,
      insertReplace: ci?.insertReplaceSupport ?? false,
      snippets: ci?.snippetSupport ?? false,
    };

    return {
      capabilities: {
        textDocumentSync: TextDocumentSyncKind.Incremental,
        definitionProvider: true,
        typeDefinitionProvider: true,
        referencesProvider: true,
        documentHighlightProvider: true,
        hoverProvider: true,
        documentSymbolProvider: true,
        workspaceSymbolProvider: true,
        renameProvider: { prepareProvider: true },
        foldingRangeProvider: true,
        documentFormattingProvider: true,
        codeActionProvider: true,
        codeLensProvider: { resolveProvider: false },
        documentLinkProvider: { resolveProvider: false },
        selectionRangeProvider: true,
        signatureHelpProvider: { triggerCharacters: ["."] },
        callHierarchyProvider: true,
        completionProvider: {
          // `"` opens the in-string slots (view include/exclude patterns,
          // classification values — P1b). `@` opens the decoration menu at a
          // declaration position (§4.3.1) — without it an editor never reopens
          // the popup on the one keystroke that names a decoration. The Monaco
          // bridge now DERIVES its trigger list from this InitializeResult
          // (P2b), so both auto-trigger on the Monaco surface too — keep this
          // the single source of truth.
          triggerCharacters: [".", " ", ">", "/", "#", "?", '"', "@"],
          resolveProvider: true,
        },
        semanticTokensProvider: {
          legend: {
            tokenTypes: [...SEMANTIC_TOKEN_TYPES],
            tokenModifiers: [...SEMANTIC_TOKEN_MODIFIERS],
          },
          full: true,
          range: false,
        },
      },
    };
  });

  // Gate diagnostic publishing until package dependencies (stdlib) are loaded.
  // `onInitialized` loads deps asynchronously; a `didOpen` processed during that
  // await would otherwise validate against an EMPTY dependency set and publish
  // pre-resolution "unknown type" diagnostics that vanish ~once deps land — a
  // visible flicker. Early publishes are suppressed; the `publishAll` flush at
  // the end of init (and on later config/watched-file changes) emits the
  // resolved set.
  let depsReady = false;

  connection.onInitialized(async () => {
    try {
      await connection.client.register(
        DidChangeWatchedFilesNotification.type,
        {
          watchers: [
            { globPattern: "**/*.arch" },
            { globPattern: "**/*.archspace" },
            { globPattern: "**/*.md" },
            { globPattern: "**/*.markdown" },
            { globPattern: "**/archlang.json" },
          ],
        }
      );
    } catch {
      // Client doesn't support dynamic registration — OK, editor edits still
      // come through textDocument sync.
    }

    try {
      const baseConfig = await resolveConfigForFolders(io, workspaceFolders);
      const merged = await mergeVscodeSettings(connection, baseConfig);
      currentConfig = merged;

      routers = [];
      for (const folderUri of workspaceFolders) {
        const router = new PackageRouter(io, folderUri, logger);
        await router.bootstrap(merged);
        routers.push(router);
      }
    } finally {
      // Deps loaded (or load failed): open the gate and flush once, even on
      // error, so diagnostics still appear instead of being suppressed forever.
      depsReady = true;
      // The bootstrap read the workspace from DISK — but any document the
      // client opened or edited WHILE it ran was routed to the fallback
      // service (routers was empty) and its setFile lost. The client believes
      // those buffers are synced and will send no further didChange, so
      // nothing else ever corrects the model. The buffer is the truth for an
      // open file: re-apply every one onto the freshly built routers.
      for (const doc of documents.all()) {
        routeToServices(doc.uri).setFile(doc.uri, doc.getText(), doc.version);
      }
      publishAll(allServices(), connection, currentLocale);
      notifyModelChanged();
    }
  });

  connection.onDidChangeConfiguration(async () => {
    const baseConfig = await resolveConfigForFolders(io, workspaceFolders);
    const merged = await mergeVscodeSettings(connection, baseConfig);
    currentConfig = merged;
    for (const r of routers) r.setConfig(merged);
    // Suppress until deps are loaded — the init flush publishes the resolved set
    // (a config change racing onInitialized would otherwise flash pre-resolution
    // diagnostics, the flicker `depsReady` exists to prevent).
    if (depsReady) publishAll(allServices(), connection, currentLocale);
    notifyModelChanged();
  });

  // Debounce parse-on-type so rapid keystrokes don't republish diagnostics
  // mid-token. Matches the node server's behaviour exactly.
  const changeTimers = new Map<string, ReturnType<typeof setTimeout>>();
  const CHANGE_DEBOUNCE_MS = 100;

  const scheduleReparse = (uri: string, text: string, version: number) => {
    const existing = changeTimers.get(uri);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      changeTimers.delete(uri);
      // Routing only: this IS the flush, so going through `servicesForUri`
      // would ask it to flush a timer it has already cancelled.
      const svc = routeToServices(uri);
      svc.setFile(uri, text, version);
      if (depsReady) publishFor(uri, svc, connection, currentLocale);
      notifyModelChanged();
    }, CHANGE_DEBOUNCE_MS);
    changeTimers.set(uri, timer);
  };

  // A pending debounced reparse leaves the workspace a few keystrokes behind
  // the editor's live document, and ANY read served inside that window answers
  // about a file version on nobody's screen. `documents` (TextDocuments) is
  // always up to date, so apply the pending change synchronously before
  // serving. Diagnostics stay debounced (the expensive republish), so this
  // doesn't reintroduce per-keystroke diagnostic churn.
  //
  // Every read reaches this through `servicesForUri`; nothing calls it
  // directly any more.
  const flushPending = (uri: string): void => {
    const timer = changeTimers.get(uri);
    if (!timer) return;
    clearTimeout(timer);
    changeTimers.delete(uri);
    const doc = documents.get(uri);
    if (!doc) return;
    // Routing only — the timer is already cancelled, so re-entering the
    // flushing wrapper would be a pointless second pass.
    routeToServices(uri).setFile(uri, doc.getText(), doc.version);
    // The cancelled timer was ALSO what would have announced the rebuild. A
    // completion request fired inside the debounce window (space, `.`, `>` and
    // `"` are all trigger characters, so this is the common case while typing)
    // would otherwise apply the edit and swallow the signal — leaving a native
    // nav tree showing a model the server no longer has, with nothing due to
    // correct it. Coalesced like every other caller.
    notifyModelChanged();
  };

  documents.onDidOpen((e) => {
    // Open is immediate — no debounce — so diagnostics appear instantly once
    // dependencies are loaded. Before then (a didOpen during the onInitialized
    // dep load) the publish is suppressed: the init `publishAll` flush covers
    // this file with resolved diagnostics. See `depsReady`.
    const svc = servicesForUri(e.document.uri);
    svc.setFile(e.document.uri, e.document.getText(), e.document.version);
    if (depsReady) publishFor(e.document.uri, svc, connection, currentLocale);
    notifyModelChanged();
  });

  documents.onDidChangeContent((e) => {
    scheduleReparse(e.document.uri, e.document.getText(), e.document.version);
  });

  documents.onDidClose((e) => {
    // Keep the file in the workspace — its content on disk is still
    // load-bearing for cross-file symbols. We only remove it if it was
    // genuinely deleted (handled via didChangeWatchedFiles).
    void e;
  });

  connection.onDidChangeWatchedFiles(async (params) => {
    let configDirty = false;
    // A package.archspace add/remove/edit can change the package layout (which
    // files belong to which package, dependency edges) — re-run discovery for
    // every folder rather than incrementally patch one services.
    let layoutDirty = false;
    for (const change of params.changes) {
      if (change.uri.endsWith("/archlang.json")) {
        configDirty = true;
        continue;
      }
      if (
        !change.uri.endsWith(".arch") &&
        !change.uri.endsWith(".archspace") &&
        !isMarkdownPath(change.uri)
      )
        continue;
      if (change.uri.endsWith(".archspace")) {
        layoutDirty = true;
        continue;
      }
      if (change.type === FileChangeType.Deleted) {
        servicesForUri(change.uri).removeFile(change.uri);
        connection.sendDiagnostics({ uri: change.uri, diagnostics: [] });
      } else {
        // An OPEN document's buffer outranks the disk. Watched-file events
        // fire for reasons that have nothing to do with the user typing — a
        // git checkout, a formatter, the burst an editor delivers when the
        // client re-registers its watchers after a server restart — and
        // answering one by reading the file overwrites whatever is unsaved.
        // Nothing then corrects it: the client considers the buffer already in
        // sync, so it sends no further didChange, and every hover, definition
        // and outline answers about a version of the file that is on nobody's
        // screen. `documents` is the live buffer, so prefer it when there is
        // one and fall back to disk for files no editor has open.
        const open = documents.get(change.uri);
        if (open) {
          servicesForUri(change.uri).setFile(
            change.uri,
            open.getText(),
            open.version
          );
        } else {
          const text = await io.readFile(change.uri);
          if (text != null) servicesForUri(change.uri).setFile(change.uri, text);
        }
      }
    }
    if (configDirty) {
      const baseConfig = await resolveConfigForFolders(io, workspaceFolders);
      const merged = await mergeVscodeSettings(connection, baseConfig);
      currentConfig = merged;
      if (!layoutDirty) for (const r of routers) r.setConfig(merged);
    }
    if (layoutDirty) {
      // Build the replacements ASIDE and swap once they're ready. Emptying
      // `routers` first would route every request served during the rebuild
      // (a full per-folder disk walk) to the empty fallback service — and
      // clients cache those blank answers against the buffer version, so an
      // outline emptied this way stays empty until the next keystroke.
      const rebuilt: PackageRouter[] = [];
      for (const folderUri of workspaceFolders) {
        const router = new PackageRouter(io, folderUri, logger);
        await router.bootstrap(currentConfig);
        rebuilt.push(router);
      }
      routers = rebuilt;
      // A fresh bootstrap reads every file from disk, so every open document
      // with unsaved edits just lost them — the same revert as above, one
      // layer up. Lay the live buffers back over the top.
      for (const doc of documents.all()) {
        servicesForUri(doc.uri).setFile(doc.uri, doc.getText(), doc.version);
      }
    }
    // Gated like the other publishers: pre-init watched-file events would
    // otherwise publish before deps (stdlib) resolve. The init flush covers it.
    if (depsReady) publishAll(allServices(), connection, currentLocale);
    notifyModelChanged();
  });

  connection.onDefinition((params) => {
    return servicesForUri(params.textDocument.uri).definition(
      params.textDocument.uri,
      params.position.line,
      params.position.character
    );
  });

  connection.onTypeDefinition((params) => {
    return servicesForUri(params.textDocument.uri).typeDefinition(
      params.textDocument.uri,
      params.position.line,
      params.position.character
    );
  });

  connection.onReferences((params) => {
    return servicesForUri(params.textDocument.uri).references(
      params.textDocument.uri,
      params.position.line,
      params.position.character,
      params.context.includeDeclaration
    );
  });

  connection.onDocumentHighlight((params) => {
    return servicesForUri(params.textDocument.uri).documentHighlight(
      params.textDocument.uri,
      params.position.line,
      params.position.character
    );
  });

  connection.onHover((params) => {
    return servicesForUri(params.textDocument.uri).hover(
      params.textDocument.uri,
      params.position.line,
      params.position.character
    );
  });

  connection.onDocumentSymbol((params) => {
    return servicesForUri(params.textDocument.uri).documentSymbols(
      params.textDocument.uri
    );
  });

  connection.onWorkspaceSymbol((params) => {
    // Workspace symbols span every package in the tree.
    return allServices().flatMap((svc) => svc.workspaceSymbols(params.query));
  });

  connection.onCompletion((params) => {
    return servicesForUri(params.textDocument.uri).completion(
      params.textDocument.uri,
      params.position.line,
      params.position.character,
      clientCompletionCaps
    );
  });

  connection.onCompletionResolve((item) => {
    const uri = (item.data as { uri?: string } | undefined)?.uri;
    const svc = uri ? servicesForUri(uri) : (routers[0]?.services()[0] ?? fallback);
    return svc.resolveCompletion(item);
  });

  connection.onFoldingRanges((params) => {
    return servicesForUri(params.textDocument.uri).foldingRanges(
      params.textDocument.uri
    );
  });

  connection.onRenameRequest((params) => {
    return servicesForUri(params.textDocument.uri).rename(
      params.textDocument.uri,
      params.position.line,
      params.position.character,
      params.newName
    );
  });

  connection.onPrepareRename((params) => {
    const result = servicesForUri(params.textDocument.uri).prepareRename(
      params.textDocument.uri,
      params.position.line,
      params.position.character
    );
    if (!result) return null;
    return result.range;
  });

  connection.onDocumentFormatting((params) => {
    return servicesForUri(params.textDocument.uri).formatting(
      params.textDocument.uri,
      {
        indentSize: params.options.tabSize,
        insertSpaces: params.options.insertSpaces,
      }
    );
  });

  connection.onCodeAction((params) => {
    return servicesForUri(params.textDocument.uri).codeActions(
      params.textDocument.uri,
      params.range,
      params.context.diagnostics
    );
  });

  connection.onCodeLens((params) => {
    return servicesForUri(params.textDocument.uri).codeLens(
      params.textDocument.uri
    );
  });

  connection.onDocumentLinks((params) => {
    return servicesForUri(params.textDocument.uri).documentLinks(
      params.textDocument.uri
    );
  });

  connection.onSelectionRanges((params) => {
    return servicesForUri(params.textDocument.uri).selectionRanges(
      params.textDocument.uri,
      params.positions
    );
  });

  connection.onSignatureHelp((params) => {
    return servicesForUri(params.textDocument.uri).signatureHelp(
      params.textDocument.uri,
      params.position.line,
      params.position.character
    );
  });

  connection.languages.callHierarchy.onPrepare((params) => {
    return servicesForUri(params.textDocument.uri).prepareCallHierarchy(
      params.textDocument.uri,
      params.position.line,
      params.position.character
    );
  });
  connection.languages.callHierarchy.onIncomingCalls((params) => {
    return servicesForUri(params.item.uri).incomingCalls(params.item);
  });
  connection.languages.callHierarchy.onOutgoingCalls((params) => {
    return servicesForUri(params.item.uri).outgoingCalls(params.item);
  });

  connection.languages.semanticTokens.on((params) => {
    return servicesForUri(params.textDocument.uri).semanticTokens(
      params.textDocument.uri
    );
  });

  documents.listen(connection);
  connection.listen();
}

// ---------------------------------------------------------------------------
// Config resolution + diagnostic publication. Per-package workspace bootstrap
// lives in PackageRouter; all I/O goes through LspIO.
// ---------------------------------------------------------------------------

async function resolveConfigForFolders(
  io: LspIO,
  folders: readonly string[]
): Promise<ArchlangConfig> {
  // First folder that yields a config file wins; otherwise use the first
  // folder's defaults.
  for (const folderUri of folders) {
    try {
      const { raw, configUri } = await loadProjectConfigFromUri(io, folderUri);
      if (raw && configUri) {
        return mergeConfig(DEFAULT_CONFIG, raw, {
          projectRoot: safeUriToPath(io.dirname(configUri)),
          hasProjectFile: true,
        });
      }
    } catch {
      // fall through to next folder
    }
  }
  if (folders.length > 0) {
    return mergeConfig(DEFAULT_CONFIG, undefined, {
      projectRoot: safeUriToPath(folders[0]!),
    });
  }
  return DEFAULT_CONFIG;
}

async function mergeVscodeSettings(
  connection: Connection,
  base: ArchlangConfig
): Promise<ArchlangConfig> {
  try {
    const settings = await connection.workspace.getConfiguration("archlang");
    const raw: RawConfig = {};
    if (settings && typeof settings === "object") {
      const s = settings as Record<string, unknown>;
      if (Array.isArray(s.include)) raw.include = s.include as string[];
      if (Array.isArray(s.exclude)) raw.exclude = s.exclude as string[];
      if (s.formatter && typeof s.formatter === "object") {
        raw.formatter = s.formatter as RawConfig["formatter"];
      }
      if (s.validation && typeof s.validation === "object") {
        raw.validation = s.validation as RawConfig["validation"];
      }
    }
    // archlang.json takes precedence over VSCode settings — only fill
    // fields the project file didn't set.
    return mergeConfig(base, raw, {
      projectRoot: base.projectRoot,
      hasProjectFile: base.hasProjectFile,
    });
  } catch {
    return base;
  }
}

function publishFor(
  uri: string,
  services: ArchlangServices,
  connection: Connection,
  locale?: string
): void {
  connection.sendDiagnostics({
    uri,
    diagnostics: services.diagnostics(uri, locale),
  });
}

function publishAll(
  allServices: readonly ArchlangServices[],
  connection: Connection,
  locale?: string
): void {
  for (const services of allServices) {
    for (const uri of services.workspace.uris()) {
      publishFor(uri, services, connection, locale);
    }
    // Markdown documents (§4.15) carry their own diagnostics
    // (DOC_BARE_INTERPOLATION, unresolved doc/aspect/section refs) and aren't
    // part of `uris()`.
    for (const uri of services.workspace.documentUris()) {
      publishFor(uri, services, connection, locale);
    }
  }
}

// ---------------------------------------------------------------------------
// URI helpers (pure string manipulation — no node, no dom).
// ---------------------------------------------------------------------------

/**
 * Best-effort file:// URI → filesystem path conversion, returning
 * `undefined` for non-file URIs. We avoid pulling in `node:url` so
 * server-core stays runtime-neutral; the browser entry never sets
 * `projectRoot` anyway, so the conversion only fires in node.
 */
function safeUriToPath(uri: string): string | undefined {
  if (!uri.startsWith("file://")) return undefined;
  try {
    const decoded = decodeURI(uri.slice("file://".length));
    // file:///C:/foo → "/C:/foo" on darwin/linux; strip leading slash on
    // a windows-style drive segment to recover "C:/foo".
    if (/^\/[A-Za-z]:\//.test(decoded)) {
      return decoded.slice(1);
    }
    return decoded;
  } catch {
    return undefined;
  }
}
